import { useEffect, useRef, useState } from "react";
import { DEV_BYPASS_AUTH } from "@/lib/auth-config";
import { announce } from "@/lib/announce";
import { apiFetch, ApiError } from "@/lib/api";
import type { AIProvider } from "@/components/qorum/AIBrandLogo";

export type SpokespersonPersona =
  | "delphine"
  | "henri"
  | "akilah"
  | "eva"
  | "ren"
  | "julien"
  | "imani";

export type SaisineMode =
  | "levee"
  | "validation"
  | "marche"
  | "pricing"
  | "plan"
  | "tendances"
  | "produit"
  | "auto";

export interface PostVerdictRecap {
  saisineNumber: string;
  question: string;
  headlineSynthesis: string;
  mode: SaisineMode;
}

export interface Spokesperson {
  persona: SpokespersonPersona;
  name: string;
  role: string;
  model: string;
  aiProvider: AIProvider;
  color: string;
  initial: string;
}

export interface ThreadMessage {
  role: "user" | "spokesperson";
  text: string;
  time: string;
  isTyping?: boolean;
}

export interface PostVerdictState {
  id: string;
  verdict: PostVerdictRecap | null;
  spokesperson: Spokesperson | null;
  thread: ThreadMessage[];
  isSending: boolean;
  isLoading: boolean;
  error: string | null;
  sendMessage: (text: string) => Promise<void>;
}

export const SPOKESPERSON_BY_MODE: Record<SaisineMode, SpokespersonPersona> = {
  levee: "delphine",
  validation: "akilah",
  marche: "ren",
  pricing: "henri",
  plan: "julien",
  tendances: "eva",
  produit: "eva",
  auto: "delphine",
};

export const SPOKESPERSON_REGISTRY: Record<SpokespersonPersona, Spokesperson> = {
  delphine: {
    persona: "delphine",
    name: "Delphine",
    role: "La Stratège",
    model: "CLAUDE SONNET 4.6 / OPUS 4.8",
    aiProvider: "anthropic",
    color: "var(--leather)",
    initial: "D",
  },
  henri: {
    persona: "henri",
    name: "Henri",
    role: "Le CFO",
    model: "GPT-5.4 MINI / GPT-5.5",
    aiProvider: "openai",
    color: "var(--navy)",
    initial: "H",
  },
  akilah: {
    persona: "akilah",
    name: "Akilah",
    role: "L'Analyste de marché",
    model: "GEMINI 3.5 FLASH",
    aiProvider: "google",
    color: "#3D5A40",
    initial: "A",
  },
  eva: {
    persona: "eva",
    name: "Eva",
    role: "La Communication",
    model: "MISTRAL SMALL / LARGE",
    aiProvider: "mistral",
    color: "var(--terracotta)",
    initial: "E",
  },
  ren: {
    persona: "ren",
    name: "Ren",
    role: "L'Analyste · Avocat du Diable",
    model: "DEEPSEEK V4 FLASH",
    aiProvider: "deepseek",
    color: "#7A2E5C",
    initial: "R",
  },
  julien: {
    persona: "julien",
    name: "Julien",
    role: "Les Opérations",
    model: "PERPLEXITY SONAR / SONAR PRO",
    aiProvider: "perplexity",
    color: "#2D5A7A",
    initial: "J",
  },
  imani: {
    persona: "imani",
    name: "Imani",
    role: "La CHRO",
    model: "COHERE COMMAND A",
    aiProvider: "cohere",
    color: "#FF1744",
    initial: "IM",
  },
};

const MOCK_RECAP: PostVerdictRecap = {
  saisineNumber: "MMXXVI·087",
  question: "Faut-il lever des fonds maintenant ?",
  headlineSynthesis: "lever maintenant, en convertible · indice 0.87",
  mode: "levee",
};

const MOCK_REPLIES: Record<SpokespersonPersona, string> = {
  delphine:
    "Précisons votre interrogation. La valorisation pré-money à 8M€ que nous avons posée est un plancher, pas un point de départ négociable. <em>Restons intransigeants.</em> Si les premiers term sheets arrivent en-dessous, c'est qu'ils n'ont pas compris la nature du dossier.",
  henri:
    "Le calcul est clair : à 14 mois de runway, vous avez la marge pour négocier. <em>N'acceptez pas la première offre.</em> Demandez à voir trois term sheets minimum.",
  akilah:
    "Sur le plan juridique, la clause MFN à 20% est non négociable. <em>Faites-la inscrire noir sur blanc.</em> Sans elle, le prochain tour sera votre perte sèche.",
  eva: "Communiquez peu, communiquez juste. <em>Le silence vaut mieux qu'une fuite mal cadrée.</em> Préparez le narratif post-closing avant d'annoncer.",
  ren: "Permettez-moi de contredire le consensus. <em>Et si vous attendiez six mois ?</em> Le marché se retourne ; à 2M€ vous obtiendriez de meilleures conditions.",
  julien:
    "Côté exécution : 60 jours pour le premier senior, c'est tendu. <em>Lancez le sourcing dès demain.</em> Sinon le verdict tombe à l'eau.",
  imani:
    "Côté capital humain : verrouillons le top 3 des recrutements critiques et un rituel d'onboarding clair. <em>Une équipe désalignée brûle votre runway plus vite qu'un mauvais marché.</em>",
};

const now = () =>
  new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

export function usePostVerdict(id: string): PostVerdictState {
  const [verdict, setVerdict] = useState<PostVerdictRecap | null>(null);
  const [spokesperson, setSpokesperson] = useState<Spokesperson | null>(null);
  const [thread, setThread] = useState<ThreadMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const streamTimers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    if (DEV_BYPASS_AUTH) {
      const recap = { ...MOCK_RECAP, saisineNumber: id || MOCK_RECAP.saisineNumber };
      setVerdict(recap);
      setSpokesperson(SPOKESPERSON_REGISTRY[SPOKESPERSON_BY_MODE[recap.mode]]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    apiFetch<Record<string, unknown>>(`/api/chat/${id}/history`)
      .then((raw) => {
        if (cancelled) return;
        const saisineNumber =
          (raw["saisine_number"] as string) ??
          (raw["saisineNumber"] as string) ??
          id;
        const question = (raw["question"] as string) ?? "";
        const headlineSynthesis =
          (raw["headline_synthesis"] as string) ??
          (raw["headlineSynthesis"] as string) ??
          "";
        const mode = ((raw["mode"] as SaisineMode) ?? "auto") as SaisineMode;
        const recap: PostVerdictRecap = {
          saisineNumber,
          question,
          headlineSynthesis,
          mode,
        };
        const personaKey = SPOKESPERSON_BY_MODE[mode] ?? "delphine";
        setVerdict(recap);
        setSpokesperson(SPOKESPERSON_REGISTRY[personaKey]);

        const rawThread = raw["thread"];
        if (Array.isArray(rawThread)) {
          setThread(
            rawThread
              .map((m): ThreadMessage | null => {
                if (!m || typeof m !== "object") return null;
                const msg = m as Record<string, unknown>;
                const role = msg["role"];
                const text = msg["text"] ?? msg["content"];
                if (
                  (role !== "user" && role !== "spokesperson") ||
                  typeof text !== "string"
                ) {
                  return null;
                }
                const time =
                  (msg["time"] as string) ??
                  (msg["created_at"] as string) ??
                  "";
                return { role, text, time };
              })
              .filter((m): m is ThreadMessage => m !== null),
          );
        }
        setIsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? err.status === 404
              ? "Saisine introuvable"
              : err.message
            : err instanceof Error
              ? err.message
              : "Erreur inconnue";
        setVerdict(null);
        setSpokesperson(null);
        setError(message);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);


  useEffect(() => {
    return () => {
      streamTimers.current.forEach((t) => clearTimeout(t));
    };
  }, []);

  const prevSendingRef = useRef(false);
  useEffect(() => {
    if (prevSendingRef.current && !isSending && spokesperson) {
      announce(`${spokesperson.name} répond`, "polite");
    }
    prevSendingRef.current = isSending;
  }, [isSending, spokesperson]);

  const sendMessage = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isSending || !spokesperson) return;

    setIsSending(true);
    setThread((prev) => [
      ...prev,
      { role: "user", text: trimmed, time: now() },
      { role: "spokesperson", text: "", time: now(), isTyping: true },
    ]);

    const reply = MOCK_REPLIES[spokesperson.persona];

    await new Promise<void>((resolve) => {
      const t = setTimeout(resolve, 1500);
      streamTimers.current.push(t);
    });

    // Stream character by character (~30 chars/sec → ~33ms per char)
    setThread((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last && last.role === "spokesperson") {
        next[next.length - 1] = { ...last, isTyping: false, text: "" };
      }
      return next;
    });

    await new Promise<void>((resolve) => {
      let i = 0;
      const step = () => {
        i += 2;
        setThread((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last && last.role === "spokesperson") {
            next[next.length - 1] = { ...last, text: reply.slice(0, i) };
          }
          return next;
        });
        if (i < reply.length) {
          const t = setTimeout(step, 33);
          streamTimers.current.push(t);
        } else {
          resolve();
        }
      };
      step();
    });

    setIsSending(false);
  };

  return {
    id,
    verdict,
    spokesperson,
    thread,
    isSending,
    isLoading,
    error,
    sendMessage,
  };
}
